import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import { loginUser } from '../services/transitCalendarService';
import { validateEmail, sanitizeInput } from '../utils/passwordValidation';
import { useUser } from '../context/UserContext';

const TransitCalendarLogin = () => {
  const navigate = useNavigate();
  const { login: setUserLoggedIn } = useUser();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/my-stars';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();

    // Sanitize inputs
    const sanitizedEmail = sanitizeInput(email);

    if (!sanitizedEmail || !password) {
      setError('Please enter your email and password');
      return;
    }

    // Validate email format
    if (!validateEmail(sanitizedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Real Cognito authentication
      const result = await loginUser(sanitizedEmail, password);

      // Store real JWT tokens
      localStorage.setItem('transit-calendar-token', result.idToken);
      localStorage.setItem('transit-calendar-access-token', result.accessToken);
      localStorage.setItem('transit-calendar-refresh-token', result.refreshToken);

      // Store user data with profiles
      if (result.userData) {
        localStorage.setItem('transit-calendar-user', JSON.stringify(result.userData));
      }

      // Update React user context so the app knows we're logged in
      const userData = result.userData || {};
      const idPayload = (() => { try { return JSON.parse(atob(result.idToken.split('.')[1])); } catch { return {}; } })();
      setUserLoggedIn({
        id: userData.userId || userData.email || idPayload.email,
        userId: userData.userId || idPayload.sub,
        email: userData.email || idPayload.email,
        name: userData.name || userData.displayName || idPayload.name || (idPayload.email && idPayload.email.split('@')[0]),
        displayName: userData.displayName || userData.name || idPayload.name || (idPayload.email && idPayload.email.split('@')[0]),
        ...userData,
      });

      // Redirect to the page the user came from, or My Stars
      navigate(redirectTo);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#0a0a0f',
      py: { xs: 4, sm: 8 },
      px: { xs: 2, sm: 0 },
      display: 'flex',
      alignItems: 'center'
    }}>
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper sx={{ p: { xs: 3, sm: 4 }, bgcolor: '#1a1a2e', border: '1px solid rgba(201,168,124,0.15)' }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                color: '#ede8e0',
              }}
            >
              Welcome Back
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                textAlign: 'center',
                color: '#8a8a9a',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Log in to see your personal stars
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444', '& .MuiAlert-icon': { color: '#ef4444' } }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    color: '#ede8e0',
                    '& fieldset': { borderColor: 'rgba(201,168,124,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(201,168,124,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#c9a87c' },
                  },
                  '& .MuiInputLabel-root': { color: '#8a8a9a' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#c9a87c' },
                }}
              />

              <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    color: '#ede8e0',
                    '& fieldset': { borderColor: 'rgba(201,168,124,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(201,168,124,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#c9a87c' },
                  },
                  '& .MuiInputLabel-root': { color: '#8a8a9a' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#c9a87c' },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  background: 'linear-gradient(135deg, #c9a87c 0%, #a88a5e 100%)',
                  color: '#0a0a0f',
                  fontWeight: 700,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dbc4a0 0%, #c9a87c 100%)',
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <span>Logging in...</span>
                  </Box>
                ) : (
                  'Log In'
                )}
              </Button>
            </form>

            {/* Register Link */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#8a8a9a' }}>
                Don't have an account?{' '}
                <Button
                  onClick={() => navigate('/transit-calendar/register')}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#c9a87c' }}
                >
                  Create one
                </Button>
              </Typography>
            </Box>

            {/* Forgot Password (Future feature) */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#6a6a7a' }}>
                Forgot your password? Contact support
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TransitCalendarLogin;
